import {
  HubspotIntegrationTypeEnum,
  HubspotTableRowFormatEnum,
  IntegrationEnum,
  MetadataContextEnum,
  MetadataSourceEnum
} from 'gql/graphql';
import { toaster } from 'helpers/toaster';
import { zip } from 'lodash';
import { usePathname, useSearchParams } from 'next/navigation';
import { useHubSpotIntegration } from 'providers/HubSpotIntegrationProviders/useHubSpotIntegration';
import { useHubspotIntegrationTableRowsMutation } from 'providers/HubSpotIntegrationProviders/useHubspotIntegrationTableRowsMutation';
import { FC, useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { notifyException } from 'services/appsignal';
import { ESpansException } from 'types/ESpans';
import { KeysEnum } from 'types/KeysEnum';

import { useDebouncedCallback } from 'use-debounce';
import { csvToFile } from 'utils/csvToFile';
import { extractParam } from 'utils/paramsUtil';
import { t } from 'utils/t';
import {
  setFileToUpload,
  setMetadata,
  updateCsvDelimiter,
  updateInputField,
  useUploadingContentFlow
} from '../_actions';

interface IHubDBIntegration {
  setFileToUpload: (file: File) => void;
  setMetadata: (metadata: any) => void;
  updateCsvDelimiter: (delimiter: string) => void;
  uploadingContentFlow: () => void;
  updateInputField: (data: { field: string; value: any }) => void;
  allowedTypes: string[];
  supportedFormats: string[];
}

const HubDBIntegration: FC<IHubDBIntegration> = ({
  setMetadata,
  setFileToUpload,
  uploadingContentFlow,
  updateInputField,
  updateCsvDelimiter
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [loading, setLoading] = useState(false);
  const { data: dataHubspotIntegration } = useHubSpotIntegration({
    type: HubspotIntegrationTypeEnum.Hubdb
  });
  const { mutateAsync: hubspotIntegrationTableRows } = useHubspotIntegrationTableRowsMutation({
    onError: (error: any) => {
      notifyException(error, ESpansException.IntegrationHubSpotException);
      toaster.error(t('messages.error.hubdb_connection'));
    }
  });

  const addon = pathname.split('/translation/new/')?.at(1);
  const type = extractParam(searchParams, KeysEnum.Type, '') as MetadataContextEnum;
  const ids = extractParam(searchParams, KeysEnum.Ids, []) as string[];

  const fetchFiles = useCallback(async () => {
    if (
      addon === IntegrationEnum.Hubspot &&
      dataHubspotIntegration?.hubspotIntegration?.id &&
      type === MetadataContextEnum.Hubdb &&
      ids?.every(String) &&
      !loading
    ) {
      setLoading(true);
      const promises = ids.map((id) =>
        hubspotIntegrationTableRows({
          tableId: id,
          format: HubspotTableRowFormatEnum.Csv
        })
      );
      const allRows = await Promise.all(promises);

      zip(allRows, ids).forEach(([data, id]: [any, any]) => {
        setFileToUpload(csvToFile(data, id));
        setMetadata({
          filename: id + '.csv',
          source: MetadataSourceEnum.Hubspot,
          context: type,
          hubspotTableId: id
        });
      });
      updateCsvDelimiter(',');
      updateInputField({
        field: 'integration',
        value: IntegrationEnum.Hubdb
      });
      uploadingContentFlow();
    }
  }, [
    addon,
    dataHubspotIntegration?.hubspotIntegration?.id,
    type,
    ids,
    loading,
    updateCsvDelimiter,
    updateInputField,
    uploadingContentFlow,
    hubspotIntegrationTableRows,
    setFileToUpload,
    setMetadata
  ]);

  const debouncedMutation = useDebouncedCallback(fetchFiles, 1);

  useEffect(() => {
    debouncedMutation();
  }, [loading, addon, dataHubspotIntegration, ids, type, fetchFiles, debouncedMutation]);

  return null;
};

export default connect(
  (state: any) => {
    const { supportedFormats, allowedTypes } = state?.staticData;

    return {
      supportedFormats,
      allowedTypes
    };
  },
  {
    setFileToUpload,
    setMetadata,
    uploadingContentFlow: useUploadingContentFlow,
    updateInputField,
    updateCsvDelimiter
  }
)(HubDBIntegration);
